import React, { useState, useMemo, useEffect } from 'react';
import '../../pages/Kpi/kpi.css';
import { Chart } from 'react-google-charts';

/**
 * Interface for aggregated BS&W data by customer/producer
 */
interface AvgBswByCustomer {
	customer: string;     // Producer/customer name
	avg_bsw: number;      // Volume-weighted average BS&W percentage
}

/**
 * Props for the ProducerBarChart component
 */
interface ProducerBarChartProps {
	filteredData: any[];        // Array of filtered data records from main table
	producerSelection: string[]; // Array of selected producer names
}

/**
 * Utility function to round numbers to specified decimal places
 * Uses exponential notation to avoid floating point precision issues
 * @param value - The number to round
 * @param decimals - Number of decimal places
 * @returns Rounded number
 */
function round(value: number, decimals: number): number {
	return Number(Math.round(Number(value + 'e' + decimals)) + 'e-' + decimals);
}
/**
 * ProducerBarChart Component
 * 
 * Renders either:
 * 1. A bar chart showing volume-weighted average BS&W by producer (when multiple producers selected)
 * 2. A histogram showing BS&W distribution bins (when single producer selected)
 * 
 * Also displays statistics card with min/max BS&W, total count, and sum of gross barrels.
 * 
 * @param filteredData - Array of data records filtered by month/year and other criteria
 * @param producerSelection - Array of selected producer names for filtering
 */
const ProducerBarChart: React.FC<ProducerBarChartProps> = ({ filteredData, producerSelection }) => {
	/**
	 * Memoized calculation of volume-weighted average BS&W by producer
	 * Formula: Sum(BS&W * Gross_Barrels) / Sum(Gross_Barrels) per producer
	 * Only recalculates when filteredData changes to prevent infinite loops
	 */
	const avgBswByCustomer: AvgBswByCustomer[] = useMemo(() =>
		filteredData && Array.isArray(filteredData)
			? Object.values(
					// Reduce to accumulate totals per producer
					filteredData.reduce<{ [customer: string]: { customer: string; total: number; count: number } }>((acc, curr) => {
						const { producer, bsw, gross_barrels } = curr;
						// Skip records with missing required data
						if (!producer || bsw == null || gross_barrels == null) return acc;
						
						// Initialize producer entry if not exists
						if (!acc[producer]) {
							acc[producer] = { customer: producer, total: 0, count: 0 };
						}
						
						// Accumulate weighted totals: BS&W * Volume and total Volume
						acc[producer].total += Number(bsw) * Number(gross_barrels);
						acc[producer].count += Number(gross_barrels);
						return acc;
					}, {})
				// Calculate final weighted averages
				).map(({ customer, total, count }) => ({
					customer,
					avg_bsw: count > 0 ? Number((total / count).toFixed(3)) : 0,
				}))
			: [], [filteredData]);

	/**
	 * Memoized statistics calculation for the current filtered dataset
	 * Calculates min/max BS&W values, total record count, and sum of gross barrels
	 */
	const stats = useMemo(() => {
		if (!filteredData || filteredData.length === 0) {
			return {
				minBsw: 0,
				maxBsw: 0,
				totalCount: 0,
				sumGrossBarrels: 0,
			};
		}
		
		// Extract and validate numerical values
		const bswValues = filteredData.map(item => Number(item.bsw)).filter(v => !isNaN(v));
		const grossBarrelsValues = filteredData.map(item => Number(item.gross_barrels)).filter(v => !isNaN(v));
		
		return {
			minBsw: Math.min(...bswValues),
			maxBsw: Math.max(...bswValues),
			totalCount: filteredData.length,
			sumGrossBarrels: grossBarrelsValues.reduce((a, b) => a + b, 0),
		};
	}, [filteredData]);

	/**
	 * Chart data state - format depends on display mode:
	 * - Multi-producer: [["Producer", "BS&W Average"], [producer1, avg1], ...]
	 * - Single producer: [["BS&W Range", "Frequency"], [range1, count1], ...]
	 */
	const [chartData, setChartData] = useState<(string | number | null | object)[][]>([
		["Producer", "BS&W Average", { role: "annotation" }],
		[0, 0, 0],
	]);

	/**
	 * Main effect to update chart data based on producer selection and filtered data
	 * Handles two display modes:
	 * 1. Single producer: Creates histogram with predefined BS&W ranges
	 * 2. Multiple producers: Shows volume-weighted averages per producer
	 */
	useEffect(() => {
		if (!avgBswByCustomer) {
			setChartData([["Producer", "BS&W Average"], [0, 0]]);
			return;
		}
		
		// Single producer mode: Generate histogram data
		if (producerSelection.length === 1) {
			// Define BS&W percentage ranges for histogram binning
			const bins = [
				{ label: "<0.5%", min: 0, max: 0.5 },
				{ label: "0.5% - <1.0%", min: 0.5, max: 1.0 },
				{ label: "1.0% - <3.0%", min: 1.0, max: 3.0 },
				{ label: "3.0% - <5.0%", min: 3.0, max: 5.0 },
				{ label: "5.0% - <10.0%", min: 5.0, max: 10.0 },
				{ label: "10.0% - <30%", min: 10.0, max: 30.0 },
				{ label: "30% - <50%", min: 30.0, max: 50.0 },
				{ label: "50% - <75%", min: 50.0, max: 75.0 },
				{ label: ">=75%", min: 75.0, max: Infinity },
			];
			
			// Extract BS&W values for the selected producer
			const bswValues = filteredData
				.filter(item => item.producer === producerSelection[0])
				.map(item => Number(item.bsw))
				.filter(v => !isNaN(v));
			
			// Count occurrences in each bin
			const binCounts = bins.map(bin =>
				bswValues.filter(v => v >= bin.min && v < bin.max).length
			);
			
			// Format data for Google Charts histogram
			const data = [
				["BS&W Bin", "Count", { role: "annotation" }],
				...bins.map((bin, i) => [bin.label, binCounts[i], binCounts[i]])
			];
			setChartData(data);
		} else {
			// Multiple producer mode: Show volume-weighted averages
			const data = [
				["Producer", "BS&W Average", { role: "annotation" }],
				...avgBswByCustomer.map(item => [item.customer, item.avg_bsw, item.avg_bsw])
			];
			setChartData(data);
		}
	}, [avgBswByCustomer, producerSelection]);

	/**
	 * Memoized chart configuration options for Google Charts
	 * Adapts title and formatting based on single vs multiple producer selection
	 */
	const chartOptions = useMemo(() => ({
		title: producerSelection.length === 1 ? `BS&W Histogram for ${producerSelection[0]}` : "BS&W Average by Producer",
		legend: 'top',
		axisTitlesPosition: 'out',
		chartArea: { left: 80, right: 100, top: 40, bottom: 80 },
		bar: { groupWidth: '30%' },
		vAxis: {
			title: producerSelection.length === 1 ? 'Count' : 'BS&W Average',
			titleTextStyle: { fontSize: 16, bold: true },
			textStyle: { fontSize: 14, color: '#2973A6' },
			gridlines: { color: '#eee' },
		},
		hAxis: {
			title: producerSelection.length === 1 ? "BS&W Bin" : "Producer",
			slantedText: false,
			slantedTextAngle: 45,
			textStyle: { fontSize: 16, bold: true, color: '#222' },
			titleTextStyle: { fontSize: 16, bold: true, color: '#222' },
		},
		annotations: {
			alwaysOutside: true,
			textStyle: { fontSize: 12, bold: true, color: '#000' },
		},
	}), [producerSelection]);

	return (
		<div>
			{/* Statistics Card - Display key metrics for current data set */}
			<div className='kpi_statsCard'>
				<div className='kpi_statItem'>
					<div className='kpi_statTitle'>Min BS&W</div>
					<div className='kpi_statValue'>{stats.minBsw}</div>
				</div>
				<div className='kpi_statItem'>
					<div className='kpi_statTitle'>Max BS&W</div>
					<div className='kpi_statValue'>{stats.maxBsw}</div>
				</div>
				<div className='kpi_statItem'>
					<div className='kpi_statTitle'>Total Items</div>
					<div className='kpi_statValue'>{stats.totalCount}</div>
				</div>
				<div className='kpi_statItem'>
					<div className='kpi_statTitle'>Sum Gross Barrels</div>
					<div className='kpi_statValue'>{round(stats.sumGrossBarrels, 2)}</div>
				</div>
			</div>
			
			{/* Main Chart Display */}
			<div className='kpi_chartWrapper'>
				<Chart
					chartType='ColumnChart'
					width='100%'
					height='35rem'
					data={chartData}
					options={chartOptions}
				/>
			</div>
			
			{/* Calculation Method Information */}
            <div className='kpi_subtext'>
                BS&W chart calculated with volume weighted averages
            </div>
		</div>
	);
};

export default ProducerBarChart;
